// Realtime Firestore hooks. Each returns data plus loading/error and sync state.
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  onSnapshot,
  type DocumentReference,
  type FirestoreError,
  type Query,
} from 'firebase/firestore'
import type { WithId } from '@spark/shared'

export interface DocState<T> {
  data: WithId<T> | null
  exists: boolean
  loading: boolean
  error: FirestoreError | null
  fromCache: boolean
  pendingWrites: boolean
}

/** Subscribes to one document. Pass null to skip. `key` must change when the reference changes. */
export function useDoc<T>(ref: DocumentReference | null, key?: string): DocState<T> {
  const refKey = key ?? ref?.path ?? ''
  const [state, setState] = useState<DocState<T>>({ data: null, exists: false, loading: !!ref, error: null, fromCache: false, pendingWrites: false })
  useEffect(() => {
    if (!ref) {
      setState({ data: null, exists: false, loading: false, error: null, fromCache: false, pendingWrites: false })
      return
    }
    setState((s) => ({ ...s, loading: true, error: null }))
    return onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snap) =>
        setState({
          data: snap.exists() ? ({ id: snap.id, ...(snap.data() as T) } as WithId<T>) : null,
          exists: snap.exists(),
          loading: false,
          error: null,
          fromCache: snap.metadata.fromCache,
          pendingWrites: snap.metadata.hasPendingWrites,
        }),
      (error) => setState({ data: null, exists: false, loading: false, error, fromCache: false, pendingWrites: false }),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refKey])
  return state
}

export interface CollectionState<T> {
  data: WithId<T>[]
  loading: boolean
  error: FirestoreError | null
  fromCache: boolean
  pendingWrites: boolean
}

/**
 * Subscribes to a query. Pass null to skip. `key` must uniquely describe the query
 * (Firestore queries are not comparable), e.g. `teams:${divisionId}`.
 */
export function useCollection<T>(q: Query | null, key: string): CollectionState<T> {
  const [state, setState] = useState<CollectionState<T>>({ data: [], loading: !!q, error: null, fromCache: false, pendingWrites: false })
  const qRef = useRef(q)
  qRef.current = q
  useEffect(() => {
    const query = qRef.current
    if (!query) {
      setState({ data: [], loading: false, error: null, fromCache: false, pendingWrites: false })
      return
    }
    setState((s) => ({ ...s, loading: true, error: null }))
    return onSnapshot(
      query,
      { includeMetadataChanges: true },
      (snap) =>
        setState({
          data: snap.docs.map((d) => ({ id: d.id, ...(d.data() as T) }) as WithId<T>),
          loading: false,
          error: null,
          fromCache: snap.metadata.fromCache,
          pendingWrites: snap.metadata.hasPendingWrites,
        }),
      (error) => setState({ data: [], loading: false, error, fromCache: false, pendingWrites: false }),
    )
  }, [key])
  return state
}

/** Map of id → document for a collection state. */
export function useById<T>(items: WithId<T>[]): Map<string, WithId<T>> {
  return useMemo(() => new Map(items.map((i) => [i.id, i])), [items])
}

/** Current time, updated every `intervalMs`. */
export function useNow(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs)
    return () => clearInterval(t)
  }, [intervalMs])
  return now
}

/** Browser online/offline state. */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine))
  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])
  return online
}

/** Value that only updates after `ms` without changes. */
export function useDebounced<T>(value: T, ms = 250): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

/** True while the media query matches, e.g. useMediaQuery('(min-width: 1024px)'). */
export function useMediaQuery(q: string): boolean {
  const [matches, setMatches] = useState(() => (typeof window === 'undefined' ? false : window.matchMedia(q).matches))
  useEffect(() => {
    const mql = window.matchMedia(q)
    const on = () => setMatches(mql.matches)
    on()
    mql.addEventListener('change', on)
    return () => mql.removeEventListener('change', on)
  }, [q])
  return matches
}
